import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";

interface Cluster {
  id: string;
  label: string;
  description?: string;
  image_url?: string;
  subCategories: SubCategory[];
}

interface SubCategory {
  id: string;
  label: string;
  description?: string;
}

interface PollFormProps {
  clusters: Cluster[];
  allocations: Record<string, number>;
  subAllocations: Record<string, number>;
  onClusterChange: (clusterId: string, value: number) => void;
  onSubChange: (subId: string, value: number) => void;
  totalPoints: number;
}

export const PollForm = ({
  clusters,
  allocations,
  subAllocations,
  onClusterChange,
  onSubChange,
  totalPoints,
}: PollFormProps) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggleExpand = (clusterId: string, shouldExpand: boolean) => {
    setExpanded((prev) => ({ ...prev, [clusterId]: shouldExpand }));
  };

  return (
    <TooltipProvider>
      <Card className="p-4 space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-1">Allocate Your 100 Points</h3>
          <p className="text-sm text-muted-foreground">
            Distribute 100 points across clusters and their subcategories.
          </p>
          <p className="text-xs text-muted-foreground mt-1">Total allocated: {totalPoints}/100</p>
        </div>

        {/* Unified two-column grid for all clusters + subs */}
        <div className="grid grid-cols-[2.75rem_1fr] gap-x-2 gap-y-3">
          {clusters.map((cluster) => {
            const clusterValue = allocations[cluster.id] || 0;
            const isExpanded = clusterValue > 0;
            const subList = cluster.subCategories;

            return (
              <>
                {/* Cluster row */}
                <div>
                  {cluster.image_url && (
                    <img src={cluster.image_url} alt={cluster.label} className="w-10 h-10 object-cover rounded-md" />
                  )}
                </div>

                <div className="space-y-3">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <span className="font-medium text-base">{cluster.label}</span>
                      {cluster.description && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-4 h-4 text-muted-foreground cursor-pointer" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs text-sm">{cluster.description}</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground whitespace-nowrap">{clusterValue} pts</div>
                  </div>

                  {/* Cluster slider */}
                  <div className="absolute inset-0 h-2 rounded-full bg-gray-300 dark:bg-gray-700" />
                  <Slider
                    value={[clusterValue]}
                    max={100}
                    step={1}
                    onValueChange={(val) => {
                      onClusterChange(cluster.id, val[0]);
                      toggleExpand(cluster.id, val[0] > 0);
                    }}
                    className="relative z-10
                               [&>[role=track]]:bg-gray-300
                               dark:[&>[role=track]]:bg-gray-700
                               [&>[role=track]]:h-2
                               [&>[role=track]]:rounded-full
                               [&>[role=range]]:bg-blue-500
                               [&>[role=range]]:rounded-full"
                  />

                  {/* Subcategories (auto-expand) */}
                  {isExpanded &&
                    subList.map((sub) => {
                      const subValue = subAllocations[sub.id] || 0;
                      const maxWidth = clusterValue;

                      return (
                        <>
                          <div /> {/* empty spacer cell in first column */}
                          <div key={sub.id} className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-1">
                                <span>{sub.label}</span>
                                {sub.description && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="w-3 h-3 text-muted-foreground cursor-pointer" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="max-w-xs text-sm">{sub.description}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground whitespace-nowrap">{subValue} pts</div>
                            </div>

                            {/* Sub slider container limited to cluster allocation width */}
                            <div className="relative mt-3" style={{ width: `${clusterValue}%` }}>
                              {/* always-visible base line */}
                              <div className="absolute inset-0 h-2 rounded-full bg-gray-300 dark:bg-gray-700" />
                              <Slider
                                value={[subValue]}
                                max={clusterValue}
                                step={1}
                                onValueChange={(val) => onSubChange(sub.id, val[0])}
                                className="relative z-10
                                           [&>[role=track]]:!bg-gray-300
                                           dark:[&>[role=track]]:!bg-gray-700
                                           [&>[role=track]]:h-2
                                           [&>[role=track]]:rounded-full
                                           [&>[role=range]]:!bg-indigo-400
                                           [&>[role=range]]:rounded-full"
                              />
                            </div>
                          </div>
                        </>
                      );
                    })}
                </div>
              </>
            );
          })}
        </div>
      </Card>
    </TooltipProvider>
  );
};
